<script>
    // ===============================
    // IMPORTS
    // ===============================
    import * as d3 from 'd3';
    import { capfirst } from 'journalize';
    import { onMount } from 'svelte';
    import tippy from 'tippy.js';
    import { formatSchoolYear } from './utils';

    // ===============================
    // COMPONENT PROPS
    // ===============================
    export let school;
    export let district;
    export let default_race;

    school = JSON.parse(school);
    district = JSON.parse(district);

    // ===============================
    // LOCAL STATE
    // ===============================
    let chartContainer;
    let legendContainer;
    let race = default_race || 'white';
    let width;
    let height = 400;

    let tooltips = [];

    /**
     * Draws a line chart plotting a private school and local public school district's demographics over time.
     */
    const updateChart = () => {
        // Perform some data transformations
        const data = {
            school: school.data
                .map((d) => ({ ...d, nonwhite: d.white ? 100 - d.white : null }))
                .filter((d) => d.year && d[race] != null)
                .map((d) => ({ ...d, year: new Date(d.year, 0, 1) })),
            district: district.data
                .map((d) => {
                    if (race === 'nonwhite') {
                        d.ccd_nonwhite_pct = 100 - d.ccd_white_pct;
                    } else {
                        const racePct = d.ccd_total_ct ? (d[`ccd_${race}_ct`] / d.ccd_total_ct) * 100 : null;
                        d[`ccd_${race}_pct`] = racePct;
                    }
                    return d;
                })
                .filter((d) => {
                    const earliestSchoolYear = d3.min(
                        school.data.filter((d) => d[`${race}`] !== null).map((s) => s.year),
                    );

                    return d.year && d[`ccd_${race}_pct`] != null && d.year >= earliestSchoolYear;
                }),
        };

        // Set up the SVG
        const margin = { top: 25, right: 20, bottom: 70, left: 40 };

        const svgWidth = width - margin.left - margin.right;
        const svgHeight = height - margin.top - margin.bottom;

        let svg = d3.select(chartContainer).select('svg');
        if (svg.empty()) {
            svg = d3
                .select(chartContainer)
                .append('svg')
                .attr('width', width)
                .attr('height', height)
                .append('g')
                .attr('transform', `translate(${margin.left}, ${margin.top})`);

            svg.append('g').attr('class', 'axis x-axis').attr('transform', `translate(0, ${svgHeight})`);

            svg.append('g').attr('class', 'axis y-axis');

            svg.append('g').attr('class', 'axis y-axis grid');

            svg.append('g').attr('class', `school ${race}`);
            svg.append('g').attr('class', 'district');
        } else {
            svg = svg.select('g');
        }

        // Set up the X and Y scales
        const districtYearsAsDates = data.district.map((d) => new Date(d.year, 0, 1));
        const earliestSchoolYear = d3.min(data.school, (d) => d.year);
        const latestYear = d3.max([
            ...data.school.map((d) => d.year), // School years (Date objects)
            ...districtYearsAsDates, // District years (converted to Date objects)
        ]);

        const xScale = d3.scaleTime().domain([earliestSchoolYear, latestYear]).range([10, svgWidth]);

        const yScale = d3.scaleLinear().domain([0, 100]).range([svgHeight, 0]);

        // Add the axes
        const tickSpacing = 75;
        const maxTicks = Math.floor(svgWidth / tickSpacing);

        const uniqueYears = Array.from(new Set([...data.school.map((d) => d.year.getFullYear())])).sort(
            (a, b) => a - b,
        );

        svg.select('.x-axis')
            .call(
                d3
                    .axisBottom(xScale)
                    .tickValues(uniqueYears.map((year) => new Date(year, 0, 1)))
                    .tickFormat((d) => formatSchoolYear(d.getFullYear())),
            )
            // Add text-anchor: end to the last tick
            .selectAll('.tick text')
            .attr('text-anchor', 'end') // Ensures alignment after rotation
            .attr('transform', 'rotate(-60)')
            .attr('dx', '-0.4em') // Adjusts the horizontal position
            .attr('dy', '0.4em'); // Adjusts the vertical position

        svg.select('.y-axis').call(
            d3
                .axisLeft(yScale)
                .ticks(5)
                .tickSize(0)
                .tickFormat((d, i) => {
                    if (i === 0) return null;
                    return `${d}%`;
                }),
        );
        // Add gridlines
        svg.select('.y-axis.grid').call(d3.axisLeft(yScale).ticks(5).tickSize(-svgWidth).tickFormat(''));

        // Define the transition
        const lineTransition = d3.transition().duration(750).ease(d3.easeCubicInOut);

        // Update or create the school line with transition
        const schoolLineGenerator = d3
            .line()
            .x((d) => xScale(d.year))
            .y((d) => yScale(d[race]));

        const schoolGroup = svg.select('g.school');
        schoolGroup.attr('class', `school ${race}`);

        let schoolLine = schoolGroup.selectAll('.school-line').data([data.school]);

        schoolLine
            .enter()
            .append('path')
            .attr('fill', 'none')
            .attr('stroke-width', 3)
            .merge(schoolLine)
            .transition(lineTransition)
            .attr('d', schoolLineGenerator)
            .attr('class', `school-line ${race}`);

        // Update or create the district line with transition
        const districtLineGenerator = d3
            .line()
            .x((d) => xScale(new Date(d.year, 0, 1)))
            .y((d) => yScale(d[`ccd_${race}_pct`]));

        const districtGroup = svg.select('g.district');

        let districtLine = districtGroup.selectAll('.district-line').data([data.district]);

        districtLine
            .enter()
            .append('path')
            .attr('fill', 'none')
            .attr('stroke-width', 3)
            .attr('stroke-dasharray', '4 4')
            .attr('stroke-opacity', 0.75)
            .merge(districtLine)
            .transition(lineTransition)
            .attr('d', districtLineGenerator)
            .attr('class', `district-line ${race}`);

        let schoolMarkers = schoolGroup.selectAll('.circle').data(data.school);

        // Remove any old markers (exit selection)
        schoolMarkers.exit().remove();

        // Enter selection: append new markers
        schoolMarkers = schoolMarkers
            .enter()
            .append('circle')
            .attr('class', 'circle')
            .attr('r', 0)
            .attr('fill', 'white')
            .attr('cx', (d) => xScale(d.year))
            .attr('cy', (d) => yScale(d[race]))
            .attr('stroke-width', 2)
            .attr('stroke', '#fff')
            .attr('fill', 'currentColor')
            .merge(schoolMarkers);

        schoolMarkers
            .transition()
            .duration(250)
            .attr('r', 0)
            .on('end', function (_, i) {
                d3.select(this)
                    .attr('cx', (d) => xScale(d.year))
                    .attr('cy', (d) => yScale(d[race]))
                    .transition()
                    .duration(250)
                    .delay(lineTransition.duration() + i * 100)
                    .attr('r', 6);
            });

        tooltips.forEach((tooltip) => tooltip.destroy());
        tooltips = [];

        schoolMarkers.each(function (d) {
            const lea = data.district.find((lea) => lea.year === d.year.getFullYear());
            const districtValue = lea ? `${((lea[`ccd_${race}_ct`] / lea.ccd_total_ct) * 100).toFixed(1)}%` : 'N/A';

            let tooltipContent = `
        <strong>${capfirst(race)} (${formatSchoolYear(d.year.getFullYear())})</strong>
        <div>${school.name}: ${d[race].toFixed(1)}%</div>`;
            if (district.name) {
                tooltipContent += `<div>${district.name}: ${districtValue}</div>`;
            }

            const tooltipInstance = tippy(this, {
                content: tooltipContent,
                allowHTML: true,
                placement: 'top',
                arrow: true,
                animation: 'fade',
            });

            tooltips.push(tooltipInstance);
        });

        updateLegend();
    };

    // ===============================
    // EVENT HANDLERS
    // ===============================
    /**
     * Update the chart when the race/ethnicity category is changed.
     */
    const handleRaceChange = (event) => {
        race = event.target.value;
        updateChart();
    };

    /**
     * Updates the legend line's class to include the currently selected racial/ethnic category.
     */
    const updateLegend = () => {
        d3.select(legendContainer)
            .selectAll('line')
            .each(function (d, i) {
                d3.select(this).attr('class', `line ${race}`);
            });
    };

    /**
     * Redraw the chart when its container's width changes.
     */
    const handleResize = () => {
        if (chartContainer.clientWidth !== width) {
            width = chartContainer.clientWidth;
            updateChart();
        }
    };

    // ===============================
    // LIFECYCLE
    // ===============================
    onMount(() => {
        width = chartContainer.clientWidth;
        updateChart();
    });
</script>

<svelte:window on:resize={handleResize} />

<div class="category-select-container">
    <select class="menu" id="category-select" bind:value={race} on:change={handleRaceChange}>
        <option disabled>Select one…</option>
        <option value="white">White</option>
        <option value="black">Black</option>
        <option value="hispanic">Hispanic or Latino</option>
        <option value="aapi">Asian American</option>
        <option value="aian">American Indian or Alaska Native</option>
        <option value="nhpi">Native Hawaiian or other Pacific Islander</option>
        <option value="multi">Multiracial</option>
    </select>
</div>

<dl class="legend" bind:this={legendContainer}>
    <div class="legend-item">
        <dt>
            <svg width="20" height="20">
                <line x1="0" x2="20" y1="10" y2="10" stroke-width="3" />
            </svg>
        </dt>
        <dd><strong>{school.name}</strong></dd>
    </div>
    {#if district.name != ''}
        <div class="legend-item">
            <dt>
                <svg width="20" height="20">
                    <line
                        x1="0"
                        x2="20"
                        y1="10"
                        y2="10"
                        stroke-width="3"
                        stroke-dasharray="4 4"
                        stroke-opacity="0.75"
                    />
                </svg>
            </dt>
            <dd>{district.name}</dd>
        </div>
    {/if}
</dl>

<div class="chart" bind:this={chartContainer} />

<style lang="scss">
    .menu {
        border: 0;
        margin: var(--spacing0) 0;
        background-color: var(--gray-3);
        font-weight: bold;

        @media only screen and (width <= 760px) {
            width: 100%;
        }

        @media (prefers-color-scheme: dark) {
            background: var(--cool-80);
        }
    }

    .legend {
        padding: 0;
        margin: var(--spacing0) 0;
        list-style-type: none;
    }

    @media only screen and (width > 760px) {
        .legend {
            display: flex;
            align-items: center;
            justify-content: space-evenly;
        }
    }

    .legend::after {
        display: none;
    }

    .legend-item {
        display: flex;
        align-items: center;
        margin: 0 var(--spacing0); /* Space between legend items */
    }

    .legend dt {
        margin-right: var(--spacing-1); /* Space between line and text */
    }

    .legend dd {
        margin: 0; /* Remove default margin */
        line-height: 20px; /* Match the height of the SVG */
        white-space: nowrap;
    }

    .chart {
        margin: var(--spacing0) 0;
    }
</style>
